import { backOff } from 'exponential-backoff';
import { z } from 'zod';

import memorizedFetch from 'utils/memorized-fetch';
import { NonNullish } from 'utils/nullish-check';

export enum CouponObjectType {
  COLLECTION = 'Collections',
  PRODUCT = 'Products',
}

export enum PointsHistoryType {
  ADDITION = 'addition',
  SUBTRACTION = 'subtraction',
}

export enum RetailOrderState {
  CLOSED = 'closed',
  PARTIALLY_RETURNED = 'partially_returned',
  CANCELLED = 'cancelled',
}

const ROOT_URI = '/apps/omnivy';

const checkReferralCode = async (code: string, burn: boolean) => {
  const Schema = z.object({
    checkReferralCode: z.boolean(),
  });
  const response = await fetch(`${ROOT_URI}/checkReferral?burnReferralCode=${burn}`, {
    method: 'post',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      referralCode: code,
    }),
  });

  if (!response.ok) {
    return false;
  }

  return Schema.parse(await response.json()).checkReferralCode;
};

export const useReferralCode = async (code: string, burn: boolean) => {
  const isCodeValid = await checkReferralCode(code, burn);

  if (!isCodeValid) {
    return false;
  }

  await fetch(`${ROOT_URI}/customerUpdateMetafield`, {
    method: 'post',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      metafieldNamespace: 'facts',
      metafieldKey: 'referral_code',
      metafieldValue: code,
    }),
  });

  return true;
};

export const getDetails = async (forceUpdate = false) => {
  const Schema = z
    .object({
      loyaltyMemberDetails: z.object({
        achievements: z.array(
          z.object({
            name: z.string(),
          }),
        ),
        loyalty: z.object({
          programs: z.record(
            z.string(),
            z.object({
              ledger: z.object({
                currentBalance: z.number(),
                pendingBalance: z.number(),
                currentTier: z.object({
                  name: z.string(),
                }),
              }),
              subLedgers: z.object({
                tier_points: z
                  .object({
                    currentBalance: z.number(),
                    pendingBalance: z.number(),
                    pointsToNextTier: z.number().optional(),
                    currentTier: z.object({
                      minPoints: z.number(),
                    }),
                  })
                  .optional(),
                noOfReferredFriends: z
                  .object({
                    currentBalance: z.number(),
                    pendingBalance: z.number(),
                  })
                  .optional(),
                totalNoOfReferredFriends: z
                  .object({
                    currentBalance: z.number(),
                    pendingBalance: z.number(),
                  })
                  .optional(),
              }),
            }),
          ),
        }),
        coupons: z.array(
          z.object({
            id: z.number(),
            value: z.string(),
            usageLimit: z.number(),
            created: z.string(),
            expiryDate: z.string().optional(),
            usageCounter: z.number(),
            recipientIntegrationId: z.string().optional(),
            attributes: z
              .object({
                couponCost: z.number().optional(),
                referralAdvocateReward: z.boolean().optional(),
                referralFriendReward: z.boolean().optional(),
              })
              .and(
                z.union([
                  z.object({
                    shopifyObjectType: z.nativeEnum(CouponObjectType),
                    shopifyObjectHandles: z.array(z.string()),
                    variantsCouponCost: z.array(z.string()).optional(),
                  }),
                  z.object({}),
                ]),
              ),
          }),
        ),
        referrals: z.array(
          z.object({
            created: z.string(),
            startDate: z.string().optional(),
            expiryDate: z.string().optional(),
            code: z.string(),
            usageLimit: z.number(),
            usageCounter: z.number(),
          }),
        ),
        profile: z.object({
          integrationId: z.string(),
        }),
      }),
    })
    .transform((data) => {
      const now = new Date();

      const program = NonNullish(Object.values(data.loyaltyMemberDetails.loyalty.programs).at(0));
      const ledger = program.ledger;
      const tierPointsLedger = program.subLedgers.tier_points;
      const referredFriendsProgress = program.subLedgers.noOfReferredFriends;
      const referredFriends = program.subLedgers.totalNoOfReferredFriends;
      const coupons = data.loyaltyMemberDetails.coupons.map((coupon) => {
        const baseCoupon = {
          title: coupon.value,
          price: coupon.attributes?.couponCost ?? 0,
          used: coupon.usageCounter >= coupon.usageLimit,
          createdAt: new Date(coupon.created),
          expiresAt: coupon.expiryDate ? new Date(coupon.expiryDate) : null,
          expired: coupon.expiryDate ? new Date(coupon.expiryDate) <= new Date() : false,
          isPublic: !coupon.recipientIntegrationId,
          isAdvocateReward: coupon.attributes.referralAdvocateReward,
          isFriendReward: coupon.attributes.referralFriendReward,
        };

        if ('shopifyObjectType' in coupon.attributes) {
          return {
            ...baseCoupon,
            ...(coupon.attributes.variantsCouponCost
              ? { variantsCouponCost: coupon.attributes.variantsCouponCost }
              : null),
            type: coupon.attributes.shopifyObjectType,
            handle: NonNullish(coupon.attributes.shopifyObjectHandles.at(0)),
          };
        }

        return baseCoupon;
      });
      const referrals = data.loyaltyMemberDetails.referrals
        .map((referral) => ({
          ...referral,
          createdAt: new Date(referral.created),
          startDate: referral.startDate ? new Date(referral.startDate) : null,
          expiryDate: referral.expiryDate ? new Date(referral.expiryDate) : null,
        }))
        .filter((referral) => {
          if (referral.usageLimit === 0) {
            return true;
          }

          return referral.usageLimit > referral.usageCounter;
        })
        .filter((referral) => {
          if (!referral.startDate || !referral.expiryDate) {
            return true;
          }

          return referral.startDate < now && referral.expiryDate > now;
        });

      referrals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      return {
        id: data.loyaltyMemberDetails.profile.integrationId,
        tier: ledger.currentTier.name.toLowerCase() as 'bronze' | 'silver' | 'gold',
        balance: ledger.currentBalance,
        pendingBalance: ledger.pendingBalance,
        cumulativeBalance: tierPointsLedger
          ? tierPointsLedger.currentBalance + tierPointsLedger.pendingBalance
          : 0,
        currentTierMinPoints: tierPointsLedger?.currentTier.minPoints ?? 0,
        pointsToNextTier: tierPointsLedger?.pointsToNextTier ?? 0,
        referredFriendsProgress: referredFriendsProgress?.currentBalance ?? 0,
        pendingReferredFriendsProgress: referredFriendsProgress?.pendingBalance ?? 0,
        totalReferredFriendsProgress: referredFriends?.currentBalance ?? 0,
        totalPendingReferredFriendsProgress: referredFriends?.pendingBalance ?? 0,
        coupons,
        achievements: data.loyaltyMemberDetails.achievements.map(({ name }) => name),
        referral: NonNullish(referrals.at(0)),
      };
    });

  const response = await backOff(() =>
    memorizedFetch(`${ROOT_URI}/memberDetails`, undefined, forceUpdate),
  );

  return Schema.parse(await response.json());
};

export const getPointsHistory = async (page = 1) => {
  const Schema = z
    .object({
      loyaltyMemberHistory: z.object({
        hasMore: z.boolean(),
        data: z.array(
          z.object({
            created: z.string(),
            name: z.string(),
            amount: z.number(),
            type: z.nativeEnum(PointsHistoryType),
            subledgerId: z.string(),
          }),
        ),
      }),
    })
    .transform((data) => ({
      hasMore: data.loyaltyMemberHistory.hasMore,
      data: data.loyaltyMemberHistory.data
        .filter((entry) => entry.subledgerId === '')
        .map((entry) => ({
          createdAt: new Date(entry.created),
          type: entry.type,
          title: entry.name,
          amount: entry.amount,
        })),
    }));

  const response = await backOff(() =>
    memorizedFetch(`${ROOT_URI}/memberDetails/loyaltyHistory?skip=${(page - 1) * 50}`),
  );

  return Schema.parse(await response.json());
};

export const getRetailOrders = async (page = 1) => {
  const Schema = z
    .object({
      hasMore: z.boolean(),
      data: z.array(
        z.object({
          orderId: z.string(),
          storeId: z.string(),
          state: z.nativeEnum(RetailOrderState),
          created: z.string(),
          cartItems: z.array(
            z.object({
              sku: z.string(),
              name: z.string(),
              price: z.number(),
              quantity: z.number(),
              returnedQuantity: z.number(),
              remainingQuantity: z.number(),
              attributes: z.object({
                itemPriceAfterDiscounts: z.number(),
              }),
            }),
          ),
        }),
      ),
    })
    .transform((data) => ({
      hasMore: data.hasMore,
      data: data.data.map((entry) => ({
        ...entry,
        createdAt: new Date(entry.created),
      })),
    }));

  const response = await backOff(() =>
    memorizedFetch(`${ROOT_URI}/memberDetails/retailOrders?skip=${(page - 1) * 50}`),
  );

  return Schema.parse(await response.json());
};

export const buyCoupon = async (
  item: {
    title: string;
    price: number;
    description?: string;
    variantsCouponCost?: Array<string>;
  },
  selectedVariantTitle?: string,
) => {
  const Schema = z
    .object({
      eventProcessingResult: z.object({
        createdCoupons: z
          .array(
            z.object({
              value: z.string(),
              attributes: z
                .object({
                  couponCost: z.number(),
                })
                .and(
                  z.union([
                    z.object({
                      shopifyObjectType: z.nativeEnum(CouponObjectType),
                      shopifyObjectHandles: z.array(z.string()),
                    }),
                    z.object({}),
                  ]),
                ),
            }),
          )
          .optional(),
      }),
    })
    .transform(
      (value) =>
        value.eventProcessingResult.createdCoupons?.map((coupon) => {
          const baseCoupon = {
            code: coupon.value,
          };

          if ('shopifyObjectType' in coupon.attributes) {
            return {
              ...baseCoupon,
              type: coupon.attributes.shopifyObjectType,
              handle: NonNullish(coupon.attributes.shopifyObjectHandles.at(0)),
            };
          }

          return baseCoupon;
        }) ?? [],
    );

  let selectedVariantsCouponCost = null;

  if (selectedVariantTitle && item.variantsCouponCost) {
    selectedVariantsCouponCost = item.variantsCouponCost
      .find((str) => str.includes(`${selectedVariantTitle}${Shopify.currency.active}:`))
      ?.split(':')[0];
  }

  const memberBuyPayload = {
    couponCode: item.title,
    ...(selectedVariantsCouponCost
      ? { attributes: { variant: selectedVariantsCouponCost } }
      : null),
  };

  const data = await fetch(`${ROOT_URI}/memberRewards/buy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(memberBuyPayload),
  }).then((response) => response.json());

  return Schema.parse(data).at(0);
};
